<script setup lang="ts">
import { useNormalizedUserName } from '../composables/useNormalizedUserName';
import type { UserNameInput } from '../helpers/normalizeUserName';
import UserAvatar from './UserAvatar.vue';
import RoleBadge from './RoleBadge.vue';
import Skeleton from 'primevue/skeleton';
import {
    isUserRole,
    type AnyRole,
} from '@precon-tools-suite-by-shared/js-precon-rbac';
import { computed } from 'vue';
import { usePreconUserProfilesOptions } from '../composables/usePreconUserProfilesOptions';

const props = withDefaults(
    defineProps<{
        userName: UserNameInput;
        email?: string | null;
        loading?: boolean;
        roles?: AnyRole[];
        rolesLoading?: boolean;
    }>(),
    {
        email: null,
        roles: () => [],
    }
);

const options = usePreconUserProfilesOptions();

const userName = useNormalizedUserName(() => props.userName);

const listedRoles = computed(() => props.roles.filter(isUserRole));
</script>

<template>
    <div class="user-account-summary flex">
        <UserAvatar
            :user-name="userName"
            :loading="loading"
            class="mr-2 text-primary xl flex-shrink-0"
        />

        <div>
            <h5 class="mb-0">
                <template v-if="userName.full">
                    {{ userName.full }}
                </template>

                <Skeleton v-else-if="props.loading" />

                <span v-else class="font-italic text-400 font-normal">
                    {{ options.noUserNamePlaceholder }}
                </span>
            </h5>
            <div v-if="props.email" class="user-account-email">
                {{ props.email }}
            </div>
            <div class="flex gap-1 py-1">
                <Skeleton v-if="rolesLoading" />
                <template v-else>
                    <RoleBadge
                        v-for="role of listedRoles"
                        :key="role"
                        :role="role"
                    />
                </template>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.user-account-summary {
    align-items: center;
}

.user-account-email {
    word-wrap: break-word;
    max-width: 75%;
}
</style>
